import { useEffect, useMemo, useState } from "react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

import {
  isValidObjectiveCustomRange,
  resolveObjectivePreset,
  type ObjectiveRangePreset,
} from "../data/objective-range";
import type { ObjectivesRange } from "../data/queries";

type RangeMode = ObjectiveRangePreset | "custom";

const MODES: ReadonlyArray<{ id: RangeMode; label: string }> = [
  { id: "7d", label: "7 days" },
  { id: "30d", label: "30 days" },
  { id: "90d", label: "90 days" },
  { id: "custom", label: "Custom" },
];

/**
 * The reporting window for the Objectives tab.
 *
 * Presets end at `max_fact_date`, not at today: the last few days of Amazon
 * sales are still maturing under 14-day attribution, so a window ending today
 * shows a trailing dip that is an artefact of the clock rather than of the ads.
 */
export function ObjectiveRangePicker({
  maxFactDate,
  onChange,
}: {
  maxFactDate: string | null;
  onChange: (range: ObjectivesRange) => void;
}) {
  const [mode, setMode] = useState<RangeMode>("30d");
  // Whether the operator has actively picked a preset. Guards the re-resolve below so the
  // first payload does not trigger a second fetch on a tab nobody has touched.
  const [hasSelectedPreset, setHasSelectedPreset] = useState(false);
  const initialCustom = useMemo(() => resolveObjectivePreset("30d", maxFactDate), [maxFactDate]);
  const [customStart, setCustomStart] = useState(initialCustom.start);
  const [customEnd, setCustomEnd] = useState(initialCustom.end);

  useEffect(() => {
    if (mode !== "custom") {
      setCustomStart(initialCustom.start);
      setCustomEnd(initialCustom.end);
    }
  }, [initialCustom, mode]);

  // A preset chosen while the first request is still in flight resolves against TODAY,
  // because maxFactDate is still null. Once it arrives the active preset must be
  // re-emitted, or the tab keeps showing a future-shifted window whose trailing days
  // carry immature attribution.
  useEffect(() => {
    if (mode === "custom" || !maxFactDate || !hasSelectedPreset) return;
    onChange(resolveObjectivePreset(mode, maxFactDate));
  }, [maxFactDate, mode, hasSelectedPreset, onChange]);

  const customIsMissing = mode === "custom" && (!customStart || !customEnd);
  const customIsReversed = Boolean(customStart && customEnd && customStart > customEnd);

  function selectMode(nextMode: RangeMode) {
    setMode(nextMode);
    if (nextMode !== "custom") {
      setHasSelectedPreset(true);
      onChange(resolveObjectivePreset(nextMode, maxFactDate));
    }
  }

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap items-end gap-3">
        <div className="flex flex-col gap-1">
          <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Range</span>
          <div className="flex flex-wrap gap-1.5" role="group" aria-label="Objective reporting range">
            {MODES.map((option) => (
              <Button
                key={option.id}
                type="button"
                size="sm"
                variant={mode === option.id ? "default" : "outline"}
                aria-pressed={mode === option.id}
                onClick={() => selectMode(option.id)}
              >
                {option.label}
              </Button>
            ))}
          </div>
        </div>
        {mode === "custom" && (
          <div className="flex flex-wrap items-end gap-2">
            <label className="flex flex-col gap-1 text-xs font-medium text-muted-foreground">
              Start
              <Input
                type="date"
                className="min-h-9"
                value={customStart}
                onChange={(event) => setCustomStart(event.target.value)}
              />
            </label>
            <label className="flex flex-col gap-1 text-xs font-medium text-muted-foreground">
              End
              <Input
                type="date"
                className="min-h-9"
                value={customEnd}
                onChange={(event) => setCustomEnd(event.target.value)}
              />
            </label>
            <Button
              type="button"
              size="lg"
              disabled={!isValidObjectiveCustomRange(customStart, customEnd)}
              onClick={() => onChange({ start: customStart, end: customEnd })}
            >
              Apply
            </Button>
          </div>
        )}
      </div>
      {customIsReversed ? (
        <p className="text-xs font-medium text-ks-danger" role="alert">
          Start date must be on or before end date.
        </p>
      ) : customIsMissing ? (
        <p className="text-xs font-medium text-ks-danger" role="alert">
          Choose both a start date and an end date.
        </p>
      ) : null}
    </div>
  );
}
